import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import SeoMeta from '../components/SeoMeta';
import { useAuth } from '../contexts/AuthContext';
import { getHistory, savePendingPreparationPlanInputs } from '../utils/storage';
import { buildPreparationPlan } from '../utils/preparationPlan';
import { daysUntilAssessment, planBookingAction } from '../utils/assessmentBooking';
import { SERVICE_PROFILE_LIST, getServiceProfile } from '../data/serviceProfiles';
import {
  COMMERCIAL_PROFILE_IDS,
  COMMERCIAL_PROFILES,
  CANDIDATE_STAGES,
  SESSION_LENGTH_OPTIONS,
  REST_DAY_OPTIONS,
  DEFAULT_PREPARATION_PREFERENCES
} from '../data/preparationProfiles';
import { SCORE_CRITERIA } from '../data/cbatScoreRequirements';
import './PublicSeo.css';

const rolesForProfile = (profile) => {
  if (!profile?.scoreOrganizationIds?.length) return [];
  return SCORE_CRITERIA.filter((criterion) => profile.scoreOrganizationIds.includes(criterion.organization));
};

const moduleGuidePath = (family, moduleId) => (
  family === 'commercial' ? `/commercial-practice/${moduleId}` : `/raf-cbat/${moduleId}`
);

const initialFormState = (family) => ({
  ...DEFAULT_PREPARATION_PREFERENCES,
  military_enabled: family !== 'commercial',
  commercial_enabled: family === 'commercial',
  active_family: family,
  assessment_date: ''
});

const inputClassName = 'mt-2 block w-full rounded-md border border-[#334c65] bg-[#040a14] px-3 py-2.5 font-mono text-white';
const labelClassName = 'text-xs font-semibold text-[#9db1c5]';

const PreparationPlanPage = ({ family }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    user, profile, updatePreparationPreferences, updateAssessmentBooking, setAssessmentBookingStatus
  } = useAuth();
  const [formState, setFormState] = useState(() => initialFormState(family));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const history = useMemo(() => getHistory(), []);
  // The dashboard's journey card is driven by the assessment booking (it's
  // what "Change date"/"Reschedule" edit), so for a military candidate the
  // booking -- not the preferences copy -- is the date of record.
  const booking = user && family === 'military' ? profile?.assessment_booking || null : null;
  const bookingIsCurrent = Boolean(booking?.date) && daysUntilAssessment(booking.date) >= 0;

  // A returning, logged-in visitor sees their own saved choices instead of
  // blank defaults -- but only when they're on the route matching their own
  // active family, so a commercial account visiting the military builder
  // (or vice versa) still starts from that family's own defaults.
  useEffect(() => {
    if (!user) return;
    const bookingDate = bookingIsCurrent ? booking.date : '';
    if (profile?.preparation_preferences?.active_family === family) {
      setFormState({
        ...profile.preparation_preferences,
        assessment_date: bookingDate || profile.preparation_preferences.assessment_date || ''
      });
    } else if (bookingDate) {
      setFormState((current) => ({ ...current, assessment_date: bookingDate }));
    }
  }, [user, profile, family, booking, bookingIsCurrent]);

  const setField = (key) => (event) => {
    const { value } = event.target;
    setFormState((current) => ({
      ...current,
      [key]: key === 'training_days_per_week' || key === 'typical_session_minutes' ? Number(value) : value
    }));
  };

  const selectedServiceProfile = useMemo(
    () => (family === 'military' ? getServiceProfile(formState.military_profile) : null),
    [family, formState.military_profile]
  );
  const availableRoles = useMemo(() => rolesForProfile(selectedServiceProfile), [selectedServiceProfile]);

  const plan = useMemo(() => buildPreparationPlan({
    family,
    serviceProfileId: formState.military_profile,
    targetRoleId: formState.military_role || null,
    trainingDaysPerWeek: formState.training_days_per_week,
    typicalSessionMinutes: formState.typical_session_minutes,
    assessmentDate: formState.assessment_date || null,
    // Same anchor the dashboard uses, so this page and the dashboard always
    // show the same phases. Saving edits a current booking in place (keeping
    // its created_at); otherwise a new booking, and a new plan, starts today.
    startDate: bookingIsCurrent ? booking.created_at : null,
    history
  }), [family, formState.military_profile, formState.military_role, formState.training_days_per_week, formState.typical_session_minutes, formState.assessment_date, history, bookingIsCurrent, booking]);

  const handleSave = async () => {
    setSaveError('');
    const payload = { ...formState, assessment_date: formState.assessment_date || null };
    if (user) {
      setSaving(true);
      const { error } = await updatePreparationPreferences(payload);
      const bookingAction = error ? null : planBookingAction(payload, booking);
      let bookingError = null;
      if (bookingAction?.type === 'reschedule') {
        ({ error: bookingError } = await setAssessmentBookingStatus(bookingAction.status));
      }
      if (bookingAction && !bookingError) {
        ({ error: bookingError } = await updateAssessmentBooking(bookingAction.booking));
      }
      setSaving(false);
      if (error || bookingError) {
        setSaveError((error || bookingError).message || 'Unable to save your plan. Please try again.');
        return;
      }
      navigate(family === 'commercial' ? '/commercial/dashboard' : '/dashboard');
    } else {
      savePendingPreparationPlanInputs(payload);
      navigate('/auth?view=signup', { state: { from: location } });
    }
  };

  const kicker = family === 'commercial' ? 'Commercial pilot preparation' : 'Military aircrew preparation';
  const title = family === 'commercial'
    ? 'Build Your Commercial Pilot Aptitude Preparation Plan'
    : 'Build Your RAF / CFAST / ADF Preparation Plan';

  return (
    <main className="public-seo-page public-guide-page">
      <SeoMeta
        title={`${title} | CBAT Academy`}
        description={family === 'commercial'
          ? 'Choose your commercial pilot aptitude battery, available training time and test date to get a personalized, phase-by-phase preparation plan immediately -- no account required.'
          : 'Choose your RAF, CFAST or ADF selection system, available training time and test date to get a personalized, phase-by-phase preparation plan immediately -- no account required.'}
        path={family === 'commercial' ? '/commercial-preparation-plan' : '/preparation-plan'}
        robots="noindex,follow"
      />
      <section className="public-guide-hero">
        <div className="public-guide-hero__inner">
          <div className="public-guide-hero__copy">
            <p className="public-kicker">{kicker}</p>
            <h1>Build Your <span>Preparation Plan</span></h1>
            <p className="public-seo-lede">Choose your assessment, your available training time and your test date. You&apos;ll see a full plan immediately -- no account needed. Save it to an account any time to keep it across devices.</p>
          </div>
        </div>
      </section>

      <article className="public-seo-copy public-guide-article">
        <section aria-label="Plan inputs">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {family === 'military' ? (
              <>
                <label className={labelClassName}>
                  Service / selection system
                  <select value={formState.military_profile} onChange={setField('military_profile')} data-testid="plan-service-profile" className={inputClassName}>
                    {SERVICE_PROFILE_LIST.map((serviceProfile) => (
                      <option key={serviceProfile.id} value={serviceProfile.id}>{serviceProfile.label}</option>
                    ))}
                  </select>
                </label>
                <label className={labelClassName}>
                  Target role <span className="font-normal text-[#71869d]">(optional)</span>
                  <select value={formState.military_role || ''} onChange={setField('military_role')} data-testid="plan-target-role" className={inputClassName}>
                    <option value="">Not sure yet</option>
                    {availableRoles.map((role) => (
                      <option key={role.id} value={role.id}>{role.shortName || role.roleName}</option>
                    ))}
                  </select>
                </label>
              </>
            ) : (
              <>
                <label className={labelClassName}>
                  Aptitude battery
                  <select value={formState.commercial_profile} onChange={setField('commercial_profile')} data-testid="plan-commercial-profile" className={inputClassName}>
                    {COMMERCIAL_PROFILE_IDS.map((profileId) => (
                      <option key={profileId} value={profileId}>{COMMERCIAL_PROFILES[profileId].label}</option>
                    ))}
                  </select>
                </label>
                <label className={labelClassName}>
                  Candidate stage <span className="font-normal text-[#71869d]">(optional)</span>
                  <select value={formState.candidate_stage || ''} onChange={setField('candidate_stage')} data-testid="plan-candidate-stage" className={inputClassName}>
                    <option value="">Not sure yet</option>
                    {CANDIDATE_STAGES.map((stage) => (
                      <option key={stage.id} value={stage.id}>{stage.label}</option>
                    ))}
                  </select>
                </label>
              </>
            )}
            <label className={labelClassName}>
              Test date <span className="font-normal text-[#71869d]">(optional)</span>
              <input type="date" value={formState.assessment_date} onChange={setField('assessment_date')} data-testid="plan-assessment-date" className={inputClassName} />
            </label>
            <label className={labelClassName}>
              Training days per week
              <select value={formState.training_days_per_week} onChange={setField('training_days_per_week')} data-testid="plan-training-days" className={inputClassName}>
                {[1, 2, 3, 4, 5, 6, 7].map((days) => <option key={days} value={days}>{days}</option>)}
              </select>
            </label>
            <label className={labelClassName}>
              Typical session length
              <select value={formState.typical_session_minutes} onChange={setField('typical_session_minutes')} data-testid="plan-session-length" className={inputClassName}>
                {SESSION_LENGTH_OPTIONS.map((minutes) => <option key={minutes} value={minutes}>{`${minutes} minutes`}</option>)}
              </select>
            </label>
            <label className={labelClassName}>
              Preferred rest day <span className="font-normal text-[#71869d]">(optional)</span>
              <select value={formState.preferred_rest_day || ''} onChange={setField('preferred_rest_day')} data-testid="plan-rest-day" className={`${inputClassName} capitalize`}>
                <option value="">No preference</option>
                {REST_DAY_OPTIONS.map((day) => <option key={day} value={day}>{day[0].toUpperCase() + day.slice(1)}</option>)}
              </select>
            </label>
          </div>
        </section>

        <section aria-label="Your plan" data-testid="preparation-plan-output">
          <h2>Your plan</h2>
          {plan.notice && (
            <p className="public-content-note" data-testid="preparation-plan-notice">{plan.notice.message}</p>
          )}
          {plan.phases.map((phase) => (
            <div key={phase.id} className="plan-phase-card" data-testid={`plan-phase-${phase.id}`}>
              <p className="public-kicker">
                {phase.dateRange ? `${phase.dateRange.start} to ${phase.dateRange.end}` : (() => {
                  const startWeek = Math.ceil(phase.startDay / 7);
                  const endWeek = Math.ceil(phase.endDay / 7);
                  return startWeek === endWeek ? `Week ${startWeek}` : `Week ${startWeek}-${endWeek}`;
                })()}
              </p>
              <h3>{phase.label}</h3>
              <p>{phase.purpose}</p>
              <p className="public-content-note">
                {phase.sessionsPerWeek} sessions/week &middot; {phase.sessionMinutes} min/session &middot; ~{phase.modulesPerSession} module{phase.modulesPerSession === 1 ? '' : 's'}/session
              </p>
              <ul>
                {phase.recommendedModules.map((entry) => (
                  <li key={entry.moduleId}>
                    <Link to={moduleGuidePath(family, entry.moduleId)}>{entry.module.name}</Link>
                    {entry.reason ? ` — ${entry.reason}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </section>

        <section aria-label="Save your plan">
          {saveError && <p className="mt-2 text-xs text-[#ff9ba4]" role="alert">{saveError}</p>}
          <div className="public-actions">
            <button type="button" className="public-button public-button--primary" onClick={handleSave} disabled={saving} data-testid="preparation-plan-save">
              {saving ? 'Saving…' : (user ? 'Save to my account' : 'Create a free account to save this plan')}
            </button>
            {!user && (
              <Link className="public-button public-button--secondary" to="/auth">Already have an account? Sign in</Link>
            )}
          </div>
          <p className="public-content-note public-content-note--footer">
            This plan is built from CBAT Academy&apos;s own practice categories, not official test-provider guidance. It cannot guarantee a specific outcome on the actual assessment.
          </p>
        </section>
      </article>
    </main>
  );
};

export default PreparationPlanPage;
